import { useEffect, useRef, useState } from "react";

/**
 * Creates a blob URL for a file and manages its lifecycle.
 * Returns null when file is null.
 */
export function useBlobUrl(file: File | null): null | string {
  const [blobUrl, setBlobUrl] = useState<null | string>(null);
  const previousFile = useRef<Nullable<File>>(null);
  const blobUrlToRevoke = useRef<null | string>(null);

  useEffect(() => {
    // No file - cleanup and clear state
    if (!file) {
      if (blobUrlToRevoke.current) {
        URL.revokeObjectURL(blobUrlToRevoke.current);
        blobUrlToRevoke.current = null;
      }

      // Set state synchronously in the effect to ensure the update happens
      // in the same render cycle as the cleanup preventing stale URL usage
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBlobUrl(null);
      previousFile.current = null;

      return undefined;
    }

    // Same file instance - skip (prevent recreation on re-renders)
    if (previousFile.current === file) {
      return undefined;
    }

    // Different file - revoke old URL and create new one
    if (blobUrlToRevoke.current) {
      URL.revokeObjectURL(blobUrlToRevoke.current);
    }

    const newUrl = URL.createObjectURL(file);

    previousFile.current = file;
    blobUrlToRevoke.current = newUrl;

    setBlobUrl(newUrl);
  }, [file]);

  return blobUrl;
}
